import { useEffect } from "react";
import { Link } from "react-router-dom";
import type { DirectoryCleaner } from "@/mocks/cleanersDirectory";
import { specialtyMeta } from "@/mocks/cleanersDirectory";

type Props = {
  cleaner: DirectoryCleaner | null;
  onClose: () => void;
};

const availabilityLabels: Record<DirectoryCleaner["availability"], string> = {
  today: "Available today",
  "this-week": "Available this week",
  "next-week": "Available next week",
};

export default function CleanerProfileDrawer({ cleaner, onClose }: Props) {
  useEffect(() => {
    if (!cleaner) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [cleaner, onClose]);

  if (!cleaner) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      ></div>
      <aside className="absolute right-0 top-0 h-full w-full md:w-[480px] bg-background-50 overflow-y-auto animate-slide-in">
        <div className="relative h-64">
          <img
            src={cleaner.image}
            alt={cleaner.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent"></div>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/95 backdrop-blur text-foreground-900 hover:bg-background-50"
            aria-label="Close profile"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <div className="absolute bottom-4 left-5 right-5 text-background-50">
            <h3 className="font-heading text-2xl font-medium">{cleaner.name}</h3>
            <p className="text-sm text-background-100/90">{cleaner.role} · {cleaner.city}</p>
          </div>
        </div>

        <div className="p-5 md:p-6 space-y-6">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-xs text-foreground-600">Rating</p>
              <p className="font-heading text-lg text-foreground-950 mt-1 flex items-center justify-center gap-1">
                <i className="ri-star-fill text-secondary-500 text-sm"></i>
                {cleaner.rating}
              </p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-xs text-foreground-600">Jobs done</p>
              <p className="font-heading text-lg text-foreground-950 mt-1">{cleaner.jobs}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-xs text-foreground-600">Hourly</p>
              <p className="font-heading text-lg text-foreground-950 mt-1">${cleaner.rate}</p>
            </div>
          </div>

          <div className="bg-primary-50 border border-primary-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-calendar-event-line"></i>
            </span>
            <div>
              <p className="text-xs text-primary-800">Schedule</p>
              <p className="text-sm font-medium text-primary-900">{availabilityLabels[cleaner.availability]}</p>
            </div>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-widest text-foreground-600 mb-2">About</h4>
            <p className="text-foreground-800 text-sm leading-relaxed">{cleaner.bio}</p>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-widest text-foreground-600 mb-2">Specialties</h4>
            <div className="flex flex-wrap gap-2">
              {cleaner.specialties.map((s) => {
                const meta = specialtyMeta[s];
                if (!meta) return null;
                return (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1.5 bg-background-100 text-foreground-900 text-xs px-3 py-1.5 rounded-full"
                  >
                    <i className={meta.icon}></i>
                    {meta.label}
                  </span>
                );
              })}
            </div>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-widest text-foreground-600 mb-2">Languages</h4>
            <div className="flex flex-wrap gap-2">
              {cleaner.languages.map((l) => (
                <span
                  key={l}
                  className="inline-flex items-center bg-secondary-100 text-secondary-900 text-xs px-3 py-1.5 rounded-full"
                >
                  {l}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-widest text-foreground-600 mb-2">Verification</h4>
            <ul className="space-y-2 text-sm text-foreground-800">
              <li className="flex items-center gap-2">
                <i className="ri-shield-check-line text-primary-600"></i>
                Background-checked & ID-verified
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-graduation-cap-line text-primary-600"></i>
                In-person craft assessment passed
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-bank-card-line text-primary-600"></i>
                $2M liability coverage included
              </li>
            </ul>
          </div>

          <div className="pt-2 flex items-center gap-2">
            <Link
              to="/booking"
              className="flex-1 text-center bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-3 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
            >
              Book {cleaner.name.split(" ")[0]}
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-3 rounded-full text-sm font-medium text-foreground-800 bg-background-100 hover:bg-background-200 whitespace-nowrap"
            >
              Close
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}